import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseOwnerAudit } from "@/lib/audit/supabaseOwner.functions";

export const Route = createFileRoute("/admin/supabase-owner-audit")({
  component: SupabaseOwnerAuditPage,
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">
      No se pudo cargar la auditoría: {error.message}
    </div>
  ),
});

function SupabaseOwnerAuditPage() {
  const fn = useServerFn(getSupabaseOwnerAudit);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["supabase-owner-audit"],
    queryFn: () => fn(),
  });

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-black">Supabase · Owner Audit</h1>
        <p className="text-sm text-muted-foreground">
          Identifica la cuenta/email propietaria del proyecto Supabase conectado a VerseSport.
          Requiere rol superadmin.
        </p>
      </header>

      <button
        onClick={() => refetch()}
        className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        disabled={isFetching}
      >
        {isFetching ? "Consultando…" : "Refrescar"}
      </button>

      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && (
        <p className="text-sm text-destructive">Error: {(error as Error).message}</p>
      )}

      {data && (
        <section className="space-y-4">
          <Card title="Proyecto conectado">
            <Row k="project_ref" v={data.projectRef} />
            <Row k="URL" v={data.url} />
            <Row k="Management token" v={data.hasManagementToken ? "Configurado" : "No configurado"} />
          </Card>

          {!data.ok && (
            <Card title="Estado" tone="warn">
              <p className="text-xs">{data.reason}</p>
              {!data.hasManagementToken && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Para ver el owner/email, agrega el secret <code className="font-mono">SUPABASE_ACCESS_TOKEN</code>{" "}
                  con un Personal Access Token de{" "}
                  <a
                    className="underline"
                    href="https://supabase.com/dashboard/account/tokens"
                    target="_blank"
                    rel="noreferrer"
                  >
                    supabase.com/dashboard/account/tokens
                  </a>
                  .
                </p>
              )}
            </Card>
          )}

          {data.ok && data.project && (
            <Card title="Proyecto (Management API)">
              <Row k="Nombre" v={data.project.name} />
              <Row k="ID" v={data.project.id} />
              <Row k="Región" v={data.project.region} />
              <Row k="Creado" v={data.project.createdAt} />
              <Row k="Organization ID" v={data.project.organizationId} />
            </Card>
          )}

          {data.ok && data.organization && (
            <Card title="Organización">
              <Row k="Nombre" v={data.organization.name} />
              <Row k="ID" v={data.organization.id} />
            </Card>
          )}

          {data.ok && data.owners.length > 0 && (
            <Card title="Owner / Miembros">
              <ul className="space-y-1 text-xs">
                {data.owners.map((m, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between rounded border border-border bg-background px-2 py-1"
                  >
                    <span className="font-mono">{m.email ?? m.user_id ?? "—"}</span>
                    <span className="text-muted-foreground">{m.role_name ?? ""}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </section>
      )}
    </main>
  );
}

function Card({
  title,
  children,
  tone,
}: {
  title: string;
  children: React.ReactNode;
  tone?: "warn";
}) {
  return (
    <div
      className={
        "rounded-xl border p-4 " +
        (tone === "warn"
          ? "border-[oklch(0.7_0.18_70)] bg-[oklch(0.7_0.18_70/0.08)]"
          : "border-border bg-background/60")
      }
    >
      <h2 className="mb-2 text-sm font-black">{title}</h2>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between rounded border border-border bg-background px-2 py-1 text-xs">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono">{v || "—"}</span>
    </div>
  );
}